import { useMemo } from 'react';
import { components } from '@api/club/generated';
import { TimeSyncService } from '@services/TimeSyncService';
import { format } from 'date-fns';
import { isToday } from 'date-fns/isToday';

import { useCountdownUntill } from '@libs/hooks/useCountdownUntill';
import { DateView } from '@ui/components/DateView';
import { Timer } from '@ui/components/Timer';

import { TournamentSystem } from '@features/shared/api/typings';
import { KnockoutRoundName } from '@features/tournament/ui/components/KnockoutRoundName';

import styles from './TournamentStatusView.module.css';

/*
    ____,-------------------------------,____
    \   |          Компонент            |   /
    /___|-------------------------------|___\
*/

type Round = {
  roundName?: string | null;
  roundNumber: number;
  cycleNumber: number | null;
  status: components['schemas']['RoundStatus'];
  startTimestampMs?: number | null;
};

export type Props = {
  className?: string;
  tournament?: {
    system: TournamentSystem;
    startTimestampMs?: number | null;
    status: components['schemas']['TournamentStatus'];
    pausedTillTimestampMs?: number | null;
    rounds: Round[];
  };
};

export function TournamentStatusView({ tournament }: Omit<Props, 'className'>) {
  const time = useTournamentCountdown(tournament);
  const { roundActive, roundNext, roundTime, hasDeletedRounds } = useRounds(tournament);

  if (!tournament) return null;

  const isKnockout = tournament.system === 'Knockout';

  const { status, pausedTillTimestampMs: pausedUntil } = tournament;

  const startTime = tournament?.startTimestampMs || 0;
  const date = startTime ? <DateView date={startTime} year="hide" showTime /> : null;
  const labelNow = 'Идет сейчас';

  switch (status) {
    case 'Draft':
      return date;
    case 'NotStarted':
      if (isToday(startTime)) {
        return time ? (
          <Timer className={styles.timer} showHours seconds={time} />
        ) : (
          'Скоро начнется'
        );
      }
      return date;
    case 'InProgress':
      if (roundActive) {
        if (isKnockout && roundActive.roundName) {
          return (
            <>
              {labelNow} ·{' '}
              <KnockoutRoundName
                roundType="WinnerBranch"
                roundName={roundActive.roundName}
                preset="full"
              />
            </>
          );
        }

        return (
          <>
            {labelNow} · {roundActive.roundNumber} тур
          </>
        );
      }

      if (roundNext) {
        if (roundTime) {
          if (isKnockout && roundNext.roundName) {
            return (
              <>
                {labelNow} ·{' '}
                <KnockoutRoundName
                  roundType="WinnerBranch"
                  roundName={roundNext.roundName}
                  preset="full"
                />{' '}
                через <Timer className={styles.timer} seconds={roundTime} />
              </>
            );
          }

          return (
            <>
              {labelNow} · {roundNext.roundNumber} тур через{' '}
              <Timer className={styles.timer} seconds={roundTime} />
            </>
          );
        }

        if (isKnockout && roundNext.roundName) {
          return (
            <>
              {labelNow} · Ожидание{' '}
              <KnockoutRoundName
                roundType="WinnerBranch"
                roundName={roundNext.roundName}
                preset="full"
              />
            </>
          );
        }

        return (
          <>
            {labelNow} · Ожидание {roundNext.roundNumber} тура{' '}
          </>
        );
      }

      return labelNow;
    case 'Paused':
      return (
        <>
          {roundActive
            ? labelNow + ` · ${roundActive.roundNumber} тур · пауза после тура`
            : 'Приостановлен'}

          {pausedUntil && ` · до ${format(pausedUntil, 'HH:mm')}`}
        </>
      );
    case 'Finished':
      return (
        <>
          {hasDeletedRounds ? 'Завершен досрочно' : 'Завершен'} · <DateView date={startTime} />
        </>
      );
    case 'Deleted':
      return <>Удален · {date}</>;
    default:
      return date;
  }
}

/*
    ____,-------------------------------,____
    \   |           Запчасти            |   /
    /___|-------------------------------|___\
*/

function useTournamentCountdown(t: Props['tournament']) {
  const stop = t?.status !== 'Draft' && t?.status !== 'NotStarted';
  const timestamp = TimeSyncService.instance.toClientTimestamp(t?.startTimestampMs) ?? 0;
  const { seconds: time } = useCountdownUntill(timestamp, stop);
  return time;
}

function useRounds(t: Props['tournament']) {
  const rounds = t?.rounds || [];
  const roundActive = rounds.find((r: Round) => r.status === 'InProgress');
  const roundNext = rounds.find((r: Round) => r.status === 'New' || r.status === 'Drafted');

  const timestamp = TimeSyncService.instance.toClientTimestamp(roundNext?.startTimestampMs) ?? 0;

  const { seconds: roundTime } = useCountdownUntill(
    timestamp,
    Boolean(roundActive || !roundNext?.startTimestampMs),
  );

  const hasDeletedRounds = useMemo(() => t?.rounds.some((r) => r.status === 'Deleted'), [t]);

  return {
    roundActive,
    roundNext,
    roundTime,
    hasDeletedRounds,
  };
}
